import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import CatalogueModal from '../CatalogueModal';
import catalogueStructure from '../../data/catalogueStructure';
import { useSiteSettings } from '../../hooks/useSiteSettings';

const heroImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/kabgc5k3_6x12%20devant%20chateau.jpg",
    title: "Chapiteau 6x12 - Réception au château"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/9hdlqsvc_afc53606-cf65-473f-b415-49e9c0745fff.JPG",
    title: "Événement de nuit illuminé"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/9q96452i_2025_06_13%20-%206X12%20-%20st%20jouin%20%20%286%29.jpg",
    title: "Chapiteau 6x12 - Jardin"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/nb6hxhxz_20250524_233620.jpg",
    title: "Réception en soirée"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/okjpps1z_270m%C2%B2%20pour%20une%20c%C3%A9r%C3%A9monie.jpg",
    title: "270m² pour une cérémonie"
  }
];

// 6 Mega Categories for the right panel (sourced from canonical catalogueStructure)
const categories = catalogueStructure.map((c) => ({ name: c.name, slug: c.slug, emoji: c.emoji }));

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardVisible, setCardVisible] = useState(false);
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const settings = useSiteSettings();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const animationTimer = setTimeout(() => {
      setCardVisible(true);
    }, 300);
    return () => clearTimeout(animationTimer);
  }, []);

  const goToSlide = (index) => setCurrentSlide(index);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroImages.length);

  return (
    <section className="relative min-h-[85vh] md:min-h-[95vh] flex items-center overflow-hidden">
      {/* Slideshow Background */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((image, index) => (
          <div
            key={image.title}
            className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          >
            <img src={image.url} alt={image.title} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40 z-10" />
      </div>

      {/* Navigation Arrows */}
      <button onClick={prevSlide} className="absolute left-2 md:left-4 top-1/3 -translate-y-1/2 z-10 w-8 h-8 md:w-12 md:h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all">
        <ChevronLeft className="w-4 h-4 md:w-6 md:h-6" />
      </button>
      <button onClick={nextSlide} className="absolute right-2 md:right-4 top-1/3 -translate-y-1/2 z-10 w-8 h-8 md:w-12 md:h-12 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all">
        <ChevronRight className="w-4 h-4 md:w-6 md:h-6" />
      </button>

      {/* Dots Indicator - hidden, using arrows for navigation */}

      {/* CHANGE 1 & 2: Two-Column Layout */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 md:py-20">
        <div className="grid lg:grid-cols-5 gap-6 md:gap-8 items-center">
          {/* Left Column (60%) - Text Content */}
          <div className="lg:col-span-3">
            {/* Badge */}
            <span className="inline-block px-3 py-1.5 md:px-4 md:py-2 bg-orange-500 text-white text-xs md:text-sm font-medium rounded-full mb-4 md:mb-6 animate-pulse">
              Nouveau : Packs Tout Inclus 2026
            </span>
            
            {/* Headline */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-2 md:mb-3">
              SAS Barnum<br />
              <span className="text-orange-400">Événement</span> & Loisirs
            </h1>

            {/* Tagline */}
            <p className="text-sm md:text-lg text-orange-300 font-medium mb-4 md:mb-6 max-w-xl">
              L'excellence au service de vos événements en Normandie
            </p>
            
            {/* Description */}
            <p className="text-base md:text-xl text-gray-200 mb-6 md:mb-8 max-w-xl">
              Profitez de vos invités avec du matériel, posé ou livré, qui fera toute la différence.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={settings.catalogueUrlWithoutPrices}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all shadow-lg text-sm md:text-base"
              >
                Feuilleter le catalogue
                <ExternalLink className="w-4 h-4 md:w-5 md:h-5" />
              </a>
              <Link
                to="/devis"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-all shadow-lg text-sm md:text-base"
              >
                <Phone className="w-5 h-5" />
                Demande de Devis
              </Link>
            </div>
          </div>

          {/* Right Column (40%) - Categories Card - DESIGN AGRANDI */}
          <div className={`lg:col-span-2 hidden lg:flex justify-end transition-all duration-700 ease-out ${cardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
            <div className="relative w-full max-w-[420px]">
            {/* Decorative orange lines */}
            <div className="absolute -top-12 -right-8 z-10">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="30" y1="55" x2="38" y2="10" stroke="#f9a23b" strokeWidth="6" strokeLinecap="round"/>
                <line x1="38" y1="58" x2="68" y2="25" stroke="#f9a23b" strokeWidth="6" strokeLinecap="round"/>
                <line x1="40" y1="68" x2="72" y2="55" stroke="#f9a23b" strokeWidth="6" strokeLinecap="round"/>
              </svg>
            </div>
            
            <div 
              className="bg-white rounded-3xl shadow-2xl px-10 py-10"
              style={{ transform: 'perspective(500px) rotateY(-3deg)' }}
            >
              <ul className="space-y-4">
                {categories.map((cat, index) => (
                  <li key={cat.name}>
                    <Link 
                      to={`/catalogue/${cat.slug}`}
                      className={`font-black text-base tracking-tight hover:text-orange-500 transition-all duration-500 flex items-center gap-2 ${cat.slug === 'pack-tout-inclus' ? 'text-orange-500' : ''} ${cardVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                      style={{ 
                        transitionDelay: `${200 + index * 80}ms`,
                        color: cat.slug === 'pack-tout-inclus' ? undefined : '#1a365d'
                      }}
                    >
                      <span className="text-lg">{cat.emoji}</span>
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            </div>
          </div>

          {/* Mobile Categories Card - dynamic effect */}
          <div className={`lg:hidden mt-8 transition-all duration-700 ease-out ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-5 shadow-xl">
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, index) => (
                  <Link 
                    key={cat.name}
                    to={`/catalogue/${cat.slug}`}
                    className={`flex items-center justify-between px-3 py-3 ${cat.slug === 'pack-tout-inclus' ? 'bg-orange-100 text-orange-600 font-bold' : 'bg-gray-50 text-gray-800'} text-xs font-semibold rounded-xl hover:bg-orange-500 hover:text-white transition-all duration-500 ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${300 + index * 60}ms` }}
                  >
                    <span>{cat.name}</span>
                    <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </Link>
                ))}
              </div>
              <Link 
                to="/catalogue"
                className="mt-3 w-full block text-center px-4 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-xl hover:bg-orange-600 transition-colors"
              >
                Voir tout le catalogue
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CHANGE 3: Stats Bar - 10 ans d'expérience */}
      <div className="absolute bottom-8 left-0 right-0 z-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="hidden lg:flex gap-8 bg-white/95 backdrop-blur-sm rounded-2xl px-8 py-6 shadow-xl justify-center items-center">
            <div className="text-center px-6">
              <div className="text-3xl font-bold text-orange-500">500+</div>
              <div className="text-sm text-gray-600 mt-1">Événements</div>
            </div>
            <div className="h-12 w-px bg-gray-200"></div>
            <div className="text-center px-6">
              <div className="text-3xl font-bold text-orange-500">10 ans</div>
              <div className="text-sm text-gray-600 mt-1">D'expérience</div>
            </div>
            <div className="h-12 w-px bg-gray-200"></div>
            <div className="text-center px-6">
              <div className="text-3xl font-bold text-orange-500">100%</div>
              <div className="text-sm text-gray-600 mt-1">Clients satisfaits</div>
            </div>
            <div className="h-12 w-px bg-gray-200"></div>
            <div className="text-center px-6">
              <div className="text-3xl font-bold text-orange-500">300+</div>
              <div className="text-sm text-gray-600 mt-1">Produits</div>
            </div>
            <div className="h-12 w-px bg-gray-200"></div>
            <Link
              to="/catalogue"
              className="flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all"
            >
              Voir le catalogue
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Catalogue Modal - CHANGE 10 */}
      <CatalogueModal isOpen={showCatalogueModal} onClose={() => setShowCatalogueModal(false)} />
    </section>
  );
};

export default HeroSection;
